import { useTranslations } from "@src/packages/i18n";
import type { PlanAction } from "../plan.types";
import { OrganicMiniIcon } from "./OrganicMiniIcon";
import {
  TimelineActionRow,
  TimelineSubtaskCard,
} from "./TimelineTaskComponents";
import { StepperSection } from "./Stepper";
import {
  SpermCarousel,
  type SpermCarouselItem,
} from "./SpermFreezingUnderstandStep";

export function SpermResetProgramStep({
  onAction,
}: {
  onAction: (action: PlanAction) => void;
}) {
  const t = useTranslations();
  const resetSlides: SpermCarouselItem[] = [
    {
      id: "bank-baseline",
      label: t("homepage.sperm_freezing_reset.step_1"),
      title: t("homepage.sperm_freezing_reset.bank_baseline_title"),
      body: t("homepage.sperm_freezing_reset.bank_baseline_body"),
      callout: t("homepage.sperm_freezing_reset.bank_baseline_callout"),
      icon: "sperm",
      cta: {
        label: t("homepage.sperm_freezing_reset.order_baseline_test"),
        screen: "AtHomeFertilityTesting",
        params: { focus: "semen-analysis" },
      },
    },
    {
      id: "avoid-exposures",
      label: t("homepage.sperm_freezing_reset.step_2"),
      title: t("homepage.shared.reduce_avoidable_exposures"),
      body: t("homepage.sperm_freezing_reset.avoid_exposures_body"),
      callout: t("homepage.sperm_freezing_reset.avoid_exposures_callout"),
      icon: "optimization",
    },
    {
      id: "health-basics",
      label: t("homepage.sperm_freezing_reset.step_3"),
      title: t("homepage.sperm_freezing_reset.health_basics_title"),
      body: t("homepage.sperm_freezing_reset.health_basics_body"),
      icon: "concierge",
      cta: {
        label: t("homepage.sperm_freezing_reset.start_sperm_plan"),
        screen: "ProgramsScreen",
        params: { focus: "sperm-optimization" },
      },
    },
    {
      id: "supplement-evidence",
      label: t("homepage.sperm_freezing_reset.step_4"),
      title: t("homepage.sperm_freezing_reset.supplements_title"),
      body: t("homepage.sperm_freezing_reset.supplements_body"),
      callout: t("homepage.sperm_freezing_reset.supplements_callout"),
      icon: "panel",
      cta: {
        label: t("homepage.sperm_freezing_reset.review_supplements"),
        screen: "SupplementsScreen",
        params: { goal: "sperm-health" },
      },
    },
    {
      id: "retest",
      label: t("homepage.sperm_freezing_reset.step_5"),
      title: t("homepage.sperm_freezing_reset.retest_title"),
      body: t("homepage.sperm_freezing_reset.retest_body"),
      callout: t("homepage.sperm_freezing_reset.retest_callout"),
      icon: "cryo",
    },
  ];

  return (
    <StepperSection
      id="precycle-sperm-freezing.reset"
      stepLabel={t("homepage.sperm_freezing_reset.step_label")}
      showRail={false}
    >
      <TimelineSubtaskCard
        id="precycle-sperm-freezing.reset.protocol"
        title={t("homepage.sperm_freezing_reset.protocol_title")}
        subtitle={t("homepage.sperm_freezing_reset.protocol_subtitle")}
        titleIcon={<OrganicMiniIcon kind="optimization" size={34} />}
        collapsedPreviewHeight={68}
      >
        <SpermCarousel items={resetSlides} onAction={onAction} />
      </TimelineSubtaskCard>

      <TimelineActionRow
        title={t("homepage.sperm_freezing_reset.bank_or_reset")}
        subtitle={t("homepage.sperm_freezing_reset.bank_or_reset_subtitle")}
        icon={<OrganicMiniIcon kind="cryo" size={34} />}
        onPress={() =>
          onAction({
            label: t("homepage.sperm_freezing_reset.start_program"),
            screen: "ProgramsScreen",
            params: { focus: "sperm-optimization" },
          })
        }
      />
    </StepperSection>
  );
}
